import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export const APPLICATION_TYPES = [
  "costs",
  "bankStatements",
  "supplierStatements",
] as const;

export type ApplicationType = (typeof APPLICATION_TYPES)[number];

export const APPLICATION_TYPE_OPTIONS: {
  value: ApplicationType;
  label: string;
}[] = [
  { value: "costs", label: "Costs" },
  { value: "bankStatements", label: "Bank statements" },
  { value: "supplierStatements", label: "Supplier statements" },
];

export interface ApplicationTypeState {
  applicationType: ApplicationType | null;
  encryptedKey: string | null;
}

export interface SetApplicationTypePayload {
  applicationType: ApplicationType;
  encryptedKey: string;
}

const STORAGE_KEY = "applicationType";

export const parseApplicationType = (
  value: unknown,
): ApplicationType | null => {
  if (typeof value !== "string") return null;
  const normalized = value.toLowerCase().replace(/[\s_-]/g, "");
  if (normalized === "costs") return "costs";
  if (normalized === "bankstatements" || normalized === "bankstatement") {
    return "bankStatements";
  }
  if (
    normalized === "supplierstatements" ||
    normalized === "supplierstatement"
  ) {
    return "supplierStatements";
  }
  return null;
};

const persistState = (state: ApplicationTypeState) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

const loadInitialState = (): ApplicationTypeState => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return { applicationType: null, encryptedKey: null };
  }

  try {
    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === "object") {
      const encryptedKey = parsed.encryptedKey ?? parsed.encryptedId ?? null;
      if (typeof encryptedKey === "string" && encryptedKey) {
        localStorage.setItem("encryptedId", encryptedKey);
      }
      return {
        applicationType: parseApplicationType(parsed.applicationType),
        encryptedKey,
      };
    }
  } catch {
    // Older sessions stored a plain application type string.
  }

  return {
    applicationType: parseApplicationType(stored),
    encryptedKey: null,
  };
};

export const getApplicationTypeEncryptedKey = (): string | null => {
  return loadInitialState().encryptedKey;
};

const applicationTypeSlice = createSlice({
  name: "applicationType",
  initialState: loadInitialState(),
  reducers: {
    setApplicationType(state, action: PayloadAction<SetApplicationTypePayload>) {
      state.applicationType = action.payload.applicationType;
      state.encryptedKey = action.payload.encryptedKey;
      persistState({
        applicationType: action.payload.applicationType,
        encryptedKey: action.payload.encryptedKey,
      });
      if (action.payload.encryptedKey) {
        localStorage.setItem("encryptedId", action.payload.encryptedKey);
      } else {
        localStorage.removeItem("encryptedId");
      }
    },
    clearApplicationType(state) {
      state.applicationType = null;
      state.encryptedKey = null;
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem("encryptedId");
    },
  },
});

export const { setApplicationType, clearApplicationType } =
  applicationTypeSlice.actions;

export default applicationTypeSlice.reducer;
